import React, { useState } from 'react';
import { promptService } from '../../services/promptService';

/**
 * Шаблоны протокола: список слева, редактор справа. Верстка и состав по
 * «Бумаге» (docs/redesign/2026-09-04-bumaga/, попап «Мои шаблоны»): окно
 * 900×580 на бумажном фоне, две карточки, ни одной рамки.
 *
 * Раньше это было маленькое окно на один шаблон: чтобы посмотреть соседний,
 * приходилось закрывать его и лезть в список заново. Артборд держит оба
 * действия рядом — и это заодно единственное место, где видно, чем свои
 * шаблоны отличаются от системных.
 *
 * 🪤 Системный шаблон открывается только на чтение — и это не запрет ради
 * запрета: он общий для всех, правка сломала бы протоколы соседям. Путь для
 * человека тот же, что в списке шапки: «Скопировать к себе», дальше правится
 * своя копия.
 */

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3';

export default function PromptEditorModal({
  open,
  onClose,
  prompt,
  prompts = [],
  defaultPromptId,
  onSaved,
  onDeleted,
  onDefaultChanged,
  onClone,
  onUse,
}) {
  const [current, setCurrent] = useState(prompt || null);
  const [name, setName] = useState(prompt ? prompt.name : '');
  const [text, setText] = useState(prompt ? prompt.text : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const isEdit = current && current._id;
  const isSystem = current && current.type === 'system';
  const isDefault = current && current._id === defaultPromptId;

  const openPrompt = (p) => {
    setCurrent(p);
    setName(p ? p.name : '');
    setText(p ? p.text : '');
    setError(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !text.trim()) {
      setError('Заполните название и текст шаблона');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = isEdit
        ? await promptService.update(current._id, { name: name.trim(), text: text.trim() })
        : await promptService.create(name.trim(), text.trim());
      onSaved(saved);
      onClose();
    } catch (e) {
      setError('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить шаблон «' + current.name + '»?')) return;
    try {
      await promptService.remove(current._id);
      onDeleted(current._id);
      onClose();
    } catch (e) {
      setError('Ошибка удаления');
    }
  };

  const handleSetDefault = async () => {
    try {
      await promptService.updateSettings({ default_prompt_id: current._id });
      onDefaultChanged(current._id);
    } catch (e) {
      setError('Ошибка установки по умолчанию');
    }
  };

  const system = prompts.filter((p) => p.type === 'system');
  const mine = prompts.filter((p) => p.type !== 'system');

  const Row = ({ p }) => (
    <button
      type="button"
      onClick={() => openPrompt(p)}
      className={`flex items-center gap-2 rounded-[10px] px-[10px] py-2 text-left transition-colors ${
        current && current._id === p._id ? 'bg-paper-panel' : 'hover:bg-paper-panel'
      }`}
    >
      <span className="flex-1 truncate text-[13px] font-medium text-paper-ink">{p.name}</span>
      {p._id === defaultPromptId && (
        <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-paper-ink-3">
          по умолч.
        </span>
      )}
    </button>
  );

  return (
    <div onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/[0.28] p-3 md:p-8">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[900px] max-w-full h-full md:h-[580px] max-h-full overflow-y-auto md:overflow-visible grid gap-3 rounded-[20px] bg-paper-bg p-3 shadow-[0_24px_64px_rgba(0,0,0,.18)] grid-cols-[minmax(0,1fr)] max-md:grid-rows-[auto_auto] lg:grid-cols-[300px_minmax(0,1fr)]"
      >
        {/* Список */}
        <div className="flex flex-col min-h-0 max-md:max-h-[40dvh] rounded-[14px] bg-paper-card px-[10px] py-[14px]">
          <div className="flex items-baseline justify-between px-[10px] pb-[10px] pt-1">
            <span className="text-[15px] font-semibold text-paper-ink">Шаблоны протокола</span>
            <button type="button" onClick={() => openPrompt(null)}
                    className="text-[12px] font-semibold text-paper-accent">
              + Новый
            </button>
          </div>

          <div className="flex flex-1 min-h-0 flex-col gap-[2px] overflow-y-auto">
            {system.length > 0 && <div className={`${LABEL} px-[10px] pb-1 pt-2`}>Системные варианты шаблонов</div>}
            {system.map((p) => <Row key={p._id} p={p} />)}

            <div className={`${LABEL} px-[10px] pb-1 pt-3`}>Мои</div>
            {mine.map((p) => <Row key={p._id} p={p} />)}
            {mine.length === 0 && (
              <div className="px-[10px] py-[6px] text-[12px] leading-[1.4] text-paper-ink-3">
                Пока пусто. Скопируйте системный и подправьте под себя — или начните с чистого.
              </div>
            )}
          </div>
        </div>

        {/* Редактор */}
        <div className="flex flex-col min-h-0 gap-[14px] rounded-[14px] bg-paper-card px-4 py-4 md:px-[26px] md:py-[22px]">
          <div className="flex items-center justify-between gap-3">
            <span className={LABEL}>
              {isSystem ? 'Системный шаблон' : isEdit ? 'Правка шаблона' : 'Новый шаблон'}
            </span>
            <button type="button" onClick={onClose}
                    className="text-[18px] text-paper-ink-3 hover:text-paper-ink">×</button>
          </div>

          {isSystem && (
            <div className="flex items-center justify-between gap-3 rounded-[10px] bg-paper-panel px-[14px] py-[10px] text-[13px] text-paper-ink">
              <span>Системный шаблон — только просмотр.</span>
              {onClone && (
                <button type="button" onClick={() => { onClone(current._id); onClose(); }}
                        className="whitespace-nowrap underline underline-offset-[3px]">
                  Скопировать к себе
                </button>
              )}
            </div>
          )}

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSystem}
            placeholder="Название — так оно появится в списке"
            className={`w-full rounded-[10px] px-[14px] py-[10px] font-display text-[18px] font-extrabold
                        tracking-[-0.02em] text-paper-ink outline-none placeholder:font-normal
                        placeholder:text-paper-hint ${isSystem ? 'bg-paper-panel' : 'bg-paper-bg'}`}
          />

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={isSystem}
            placeholder="Инструкция модели: какие разделы нужны, что считать задачей, как подписывать участников…"
            className={`w-full flex-1 min-h-0 max-md:min-h-[40dvh] resize-none rounded-[10px] px-[14px] py-[12px] text-[14px]
                        leading-[1.6] text-paper-ink outline-none placeholder:text-paper-hint
                        ${isSystem ? 'bg-paper-panel' : 'bg-paper-bg'}`}
          />

          <div className="text-[12px] leading-[1.5] text-paper-ink-3">
            Объём (TL;DR … Максимально) и правило «только документ, без вступлений» добавляются
            к любому шаблону автоматически — писать их не нужно.
          </div>

          {error && <div className="text-[13px] text-paper-accent">{error}</div>}

          <div className="flex shrink-0 flex-wrap items-center gap-[14px]">
            {isEdit && !isSystem && (
              <button type="button" onClick={handleDelete}
                      className="text-[13px] text-paper-accent underline underline-offset-[3px]">
                Удалить
              </button>
            )}
            {isEdit && !isDefault && (
              <button type="button" onClick={handleSetDefault}
                      className="text-[13px] text-paper-ink-2 underline underline-offset-[3px]">
                Сделать по умолчанию
              </button>
            )}
            {isDefault && (
              <span className="text-[13px] text-paper-ink-3">По умолчанию для новых записей</span>
            )}

            <span className="flex-1" />

            {isEdit && onUse && (
              <button type="button" onClick={() => { onUse(current); onClose(); }}
                      className="rounded-[10px] bg-paper-bg px-4 py-[10px] text-[13px] font-medium text-paper-ink">
                Использовать
              </button>
            )}
            {!isSystem && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-[10px] bg-paper-accent px-[18px] py-[10px] text-[13px] font-semibold text-white hover:bg-paper-accent-hover disabled:bg-paper-bg disabled:text-paper-hint transition-colors"
              >
                {saving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
